import { z } from 'zod';
import { rollNoSchema } from './auth.js';
/**
 * Managing a school's people and classes (spec section 2, section 3.1
 * Platform 4): what an admin sends, and what a teacher sends to reset a PIN.
 *
 * Credentials are never part of a request here. A PIN or a starting password
 * is generated on the server and returned exactly once, so the person issuing
 * it never chooses one — which is how "1234" ends up on forty slips.
 */
/** 'YYYY-MM-DD', never a Date (CLAUDE.md, calendar dates). */
export const calendarDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2027-03-22.')
    // Date.parse rolls 2027-02-30 over to March, so round-trip it instead.
    .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}, 'That is not a real date.');
export const CLASS_LIMITS = {
    nameMaxLength: 32,
    /** A class cap of 0 turns the tutor off for that class; null follows the default. */
    turnCapMax: 200,
    /** One paste is one class; a section is ~40, so this is generous. */
    rosterMaxRows: 200,
};
export const classNameSchema = z
    .string()
    .trim()
    .min(1, 'Give the class a name, like 10-A.')
    .max(CLASS_LIMITS.nameMaxLength);
export const dailyTurnCapSchema = z
    .number()
    .int('A whole number of turns.')
    .min(0)
    .max(CLASS_LIMITS.turnCapMax, `At most ${CLASS_LIMITS.turnCapMax} turns a day.`);
export const createClassSchema = z.object({
    name: classNameSchema,
    examDate: calendarDateSchema.nullable().default(null),
    dailyTurnCap: dailyTurnCapSchema.nullable().default(null),
});
/** Every field optional; an absent one is left alone, a null one is cleared. */
export const updateClassSchema = z.object({
    name: classNameSchema.optional(),
    examDate: calendarDateSchema.nullable().optional(),
    dailyTurnCap: dailyTurnCapSchema.nullable().optional(),
});
export const personNameSchema = z
    .string()
    .trim()
    .min(1, 'A name is needed.')
    .max(80, 'Keep the name under 80 characters.');
export const createTeacherSchema = z.object({
    name: personNameSchema,
    email: z.string().trim().toLowerCase().email('That is not an email address.').max(254),
});
export const assignTeacherSchema = z.object({ teacherId: z.string().uuid() });
export const rosterRowSchema = z.object({
    name: personNameSchema,
    rollNo: rollNoSchema,
});
export const createStudentSchema = rosterRowSchema.extend({ classId: z.string().uuid() });
export const rosterSchema = z.object({
    rows: z
        .array(rosterRowSchema)
        .min(1, 'Paste at least one student.')
        .max(CLASS_LIMITS.rosterMaxRows, `At most ${CLASS_LIMITS.rosterMaxRows} students at a time.`),
});
export const moveStudentSchema = z.object({ classId: z.string().uuid() });
export const setActiveSchema = z.object({ active: z.boolean() });
/**
 * How much a cell looks like a roll number: 0 for anything that cannot be one
 * (no digit, or longer than a roll), then up to 3 for a short run of digits
 * with no letters and no spaces. A roll such as "10A-14" still scores above a
 * name such as "Rahul 2", which has a space and is mostly letters.
 */
function rollLikeness(cell) {
    if (!/\d/.test(cell) || cell.length > 16)
        return 0;
    const digits = (cell.match(/\d/g) ?? []).length;
    return 1 + (/\s/.test(cell) ? 0 : 1) + digits / cell.length;
}
export function parseRoster(text) {
    const rows = [];
    const problems = [];
    const seen = new Map();
    text.split(/\r?\n/).forEach((raw, index) => {
        const line = index + 1;
        const trimmed = raw.trim();
        if (!trimmed)
            return;
        const cells = (trimmed.includes('\t') ? trimmed.split('\t') : trimmed.split(','))
            .map((cell) => cell.trim())
            .filter(Boolean);
        if (cells.length === 2 && /^(roll|name)/i.test(cells[0] ?? '') && !/\d/.test(trimmed)) {
            return; // a header row
        }
        if (cells.length !== 2) {
            problems.push({ line, text: trimmed, reason: 'Expected a name and a roll number.' });
            return;
        }
        const [first = '', second = ''] = cells;
        // Whichever cell is more like a roll number is the roll. "Has a digit"
        // alone was not enough: "014, Rahul 2" has a digit on both sides, and the
        // tie went to the second cell, so Rahul 2 became the roll and 014 the name.
        const [rollNo, name] = rollLikeness(first) > rollLikeness(second) ? [first, second] : [second, first];
        const parsed = rosterRowSchema.safeParse({ name, rollNo });
        if (!parsed.success) {
            problems.push({
                line,
                text: trimmed,
                reason: parsed.error.issues[0]?.message ?? 'Could not read this line.',
            });
            return;
        }
        const earlier = seen.get(parsed.data.rollNo);
        if (earlier !== undefined) {
            problems.push({
                line,
                text: trimmed,
                reason: `Roll ${parsed.data.rollNo} is also on line ${earlier}.`,
            });
            return;
        }
        seen.set(parsed.data.rollNo, line);
        rows.push(parsed.data);
    });
    return { rows, problems };
}
//# sourceMappingURL=accounts.js.map